import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useSEO } from "@/hooks/useSEO";
import { Calendar, Users, MapPin, Clock, CheckCircle, ArrowRight, Star, Camera } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Gallery preview images
const GALLERY_PREVIEW = [
  "https://customer-assets.emergentagent.com/job_ec-events-booking/artifacts/k3fmts5h_1000015917.jpg",
  "https://customer-assets.emergentagent.com/job_ec-events-booking/artifacts/waaf8owz_1000015920.jpg",
  "https://customer-assets.emergentagent.com/job_ec-events-booking/artifacts/9x4wxxhj_1000015923.jpg"
];

export default function HomePage() {
  const [hallInfo, setHallInfo] = useState(null);
  const [upcomingEvents, setUpcomingEvents] = useState([]);

  useSEO({
    title: "The Elizabethan Centre | Hall Hire in Whaplode Drove, Lincolnshire",
    description: "Hire The Elizabethan Centre in Whaplode Drove for weddings, parties, meetings and community events. Affordable hall hire from £20/hour or £100/day. Capacity 150 guests. Book online today!",
    canonical: "https://www.elizabethancentre.co.uk"
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [hallRes, eventsRes] = await Promise.all([
          axios.get(`${API}/hall-info`),
          axios.get(`${API}/events?upcoming_only=true`)
        ]);
        setHallInfo(hallRes.data);
        setUpcomingEvents(eventsRes.data.slice(0, 3));
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };
    fetchData();
  }, []);

  const features = [
    {
      icon: <Users className="w-8 h-8" />,
      title: "Capacity for 150+",
      description: "Spacious main hall suitable for large gatherings and community events"
    },
    {
      icon: <MapPin className="w-8 h-8" />,
      title: "Village Location",
      description: "Easy to find with ample parking space for all your guests"
    },
    {
      icon: <Calendar className="w-8 h-8" />,
      title: "Flexible Booking",
      description: "Morning, afternoon, evening, or full-day hire options available"
    },
    {
      icon: <CheckCircle className="w-8 h-8" />,
      title: "Full Facilities",
      description: "Fully licensed bar, kitchen, stage, tables, chairs, and disabled access"
    }
  ];

  return (
    <div data-testid="home-page">
      {/* Hero Section */}
      <section className="relative h-[80vh] min-h-[600px] overflow-hidden" data-testid="hero-section">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{ 
            backgroundImage: `url('https://customer-assets.emergentagent.com/job_ec-events-booking/artifacts/waaf8owz_1000015920.jpg')`
          }}
        />
        <div className="hero-overlay absolute inset-0" />
        
        <div className="relative h-full container-custom flex items-center">
          <div className="max-w-2xl animate-fade-in">
            <h1 className="heading-xl text-white mb-6">
              The Elizabethan Centre - Whaplode Drove Social Club
            </h1>
            <p className="text-xl text-white/90 mb-4 leading-relaxed">
              A welcoming community hall and fully licensed bar open to everyone in the heart of Whaplode Drove. Perfect for weddings, parties, meetings, and celebrations.
            </p>
            <p className="text-lg text-white/80 mb-8 leading-relaxed">
              Bar is open Wednesday to Sunday and all are welcome. We also run events from live music, tribute acts, quizzes, Sunday lunches, curry nights and more. Check out our events page for more information.
            </p>
            <div className="flex flex-wrap gap-4">
              <Link to="/book">
                <Button className="btn-primary text-base" data-testid="hero-book-btn">
                  Book the Hall
                  <ArrowRight size={18} className="ml-2" />
                </Button>
              </Link>
              <Link to="/about">
                <Button className="btn-outline bg-white/10 border-white text-white hover:bg-white hover:text-[#1C1917] text-base">
                  Learn More
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="section-padding bg-[#F5F5F0]" data-testid="features-section">
        <div className="container-custom">
          <div className="text-center mb-16">
            <h2 className="heading-lg mb-4">Why Choose Our Hall?</h2>
            <p className="text-body max-w-2xl mx-auto">
              The Elizabethan Centre offers everything you need for a successful event
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature) => (
              <Card 
                key={feature.title} 
                className="card-interactive bg-white"
                data-testid={`feature-card-${feature.title.toLowerCase().replace(/\s+/g, '-')}`}
              >
                <CardContent className="p-8 text-center">
                  <div className="w-16 h-16 bg-[#14532D]/10 rounded-2xl flex items-center justify-center mx-auto mb-6 text-[#14532D]">
                    {feature.icon}
                  </div>
                  <h3 className="text-xl font-semibold font-serif mb-3 text-[#1C1917]">
                    {feature.title}
                  </h3>
                  <p className="text-subtle">
                    {feature.description}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="section-padding" data-testid="pricing-section">
        <div className="container-custom">
          <div className="text-center mb-16">
            <h2 className="heading-lg mb-4">Hire Rates</h2>
            <p className="text-body max-w-2xl mx-auto">
              Affordable rates for all your event needs. Includes tables, chairs, and kitchen facilities.
            </p>
          </div>

          {hallInfo && (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
                {Object.entries(hallInfo.pricing).map(([key, slot]) => (
                  <Card 
                    key={key} 
                    className="card-heritage"
                    data-testid={`pricing-card-${key}`}
                  >
                    <CardContent className="p-6 text-center">
                      <h3 className="text-lg font-semibold font-serif mb-2 text-[#1C1917]">
                        {slot.name}
                      </h3>
                      <div className="price-tag">
                        £{slot.price}
                      </div>
                      {slot.description && (
                        <p className="text-xs text-[#78716C] mt-2">{slot.description}</p>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </div>
              <div className="text-center">
                <Link to="/book">
                  <Button className="btn-primary text-base">
                    Book the Hall
                    <ArrowRight size={18} className="ml-2" />
                  </Button>
                </Link>
              </div>
            </>
          )}
        </div>
      </section>

      {/* Gallery Preview Section */}
      <section className="section-padding" data-testid="gallery-preview-section">
        <div className="container-custom">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-12">
            <div>
              <h2 className="heading-lg mb-2">See Our Venue</h2>
              <p className="text-body">Browse photos from events held at our hall</p>
            </div>
            <Link to="/gallery">
              <Button variant="outline" className="border-[#14532D] text-[#14532D]">
                <Camera size={16} className="mr-2" />
                View Full Gallery
              </Button>
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {GALLERY_PREVIEW.map((img, index) => (
              <Link 
                to="/gallery" 
                key={img}
                className="group relative aspect-[4/3] overflow-hidden rounded-xl"
              >
                <img
                  src={img}
                  alt={`Venue photo ${index + 1}`}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-black/30 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
                  <span className="text-white font-semibold flex items-center gap-2">
                    <Camera size={20} />
                    View Gallery
                  </span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Upcoming Events */}
      {upcomingEvents.length > 0 && (
        <section className="section-padding bg-[#F5F5F0]" data-testid="events-preview-section">
          <div className="container-custom">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-12">
              <div>
                <h2 className="heading-lg mb-2">Upcoming Events</h2>
                <p className="text-body">See what's happening at the centre</p>
              </div>
              <Link to="/events">
                <Button variant="outline" className="border-[#14532D] text-[#14532D]">
                  View All Events
                  <ArrowRight size={16} className="ml-2" />
                </Button>
              </Link>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {upcomingEvents.map((event) => (
                <Card key={event.id} className="event-card" data-testid={`event-card-${event.id}`}>
                  <div className="flex items-start gap-4">
                    <div className="bg-[#14532D] text-white rounded-lg p-3 text-center min-w-[60px]">
                      <span className="block text-2xl font-bold font-mono">
                        {new Date(event.event_date).getDate()}
                      </span>
                      <span className="block text-xs uppercase">
                        {new Date(event.event_date).toLocaleDateString('en-GB', { month: 'short' })}
                      </span>
                    </div>
                    <div className="flex-1">
                      <h3 className="font-semibold font-serif text-lg mb-1 text-[#1C1917]">
                        {event.title}
                      </h3>
                      <p className="text-subtle flex items-center gap-1 mb-2">
                        <Clock size={14} />
                        {event.start_time} - {event.end_time}
                      </p>
                      <p className="text-sm text-[#78716C] line-clamp-2">
                        {event.description}
                      </p>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA Section */}
      <section className="section-padding bg-[#14532D]" data-testid="cta-section">
        <div className="container-custom text-center">
          <div className="max-w-3xl mx-auto">
            <Star className="w-12 h-12 text-[#D97706] mx-auto mb-6" />
            <h2 className="text-3xl sm:text-4xl font-bold font-serif text-white mb-6">
              Ready to Book Your Event?
            </h2>
            <p className="text-lg text-white/80 mb-8 leading-relaxed">
              Check availability and secure your date today. Our online booking system 
              makes it easy to see available slots and submit your booking request.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link to="/book">
                <Button className="bg-white text-[#14532D] hover:bg-[#F5F5F0] h-12 px-8 text-base rounded-full font-medium">
                  Check Availability
                  <Calendar size={18} className="ml-2" />
                </Button>
              </Link>
              <Link to="/contact">
                <Button variant="outline" className="border-2 border-white text-white hover:bg-white/10 h-12 px-8 text-base rounded-full">
                  Contact Us
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
